// RelationshipCard. Mirrors data-component="relationship-card" from
// the approved HTML. One card per connection with: title band (symbol
// + organization + edit button), details grid, role tag, notes block,
// optional end-note, footer with verification pill and End or replace.
//
// Verification states this component renders (shape is the design,
// values are set upstream by services/identity-verification):
//   - "Identity not verified" (default on customer-reported records)
//   - "Details to confirm" (placeholder, missing fields)
//   - "Verified" (identity-verification returned success)
//   - "Former" (ended connection, retained for history)
//
// TODO(runtime): "Edit relationship" + "End or replace connection"
// dispatch to services/relationships once the write endpoint lands.
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import type { Relationship } from '../mockData.js';

export interface RelationshipCardProps {
  relationship: Relationship;
  linkedHorseName: string;
  onEdit: (relationship: Relationship) => void;
  onEnd: (relationship: Relationship) => void;
}

function verificationPillClass(verification: Relationship['verification']): string {
  if (verification === 'Verified') return 'verified-pill';
  if (verification === 'Former') return 'former-pill';
  return 'needs-pill';
}

export function RelationshipCard({
  relationship,
  linkedHorseName,
  onEdit,
  onEnd,
}: RelationshipCardProps): ReactNode {
  const symbolClass = `relationship-symbol${
    relationship.symbolTone && relationship.symbolTone !== 'default'
      ? ` ${relationship.symbolTone}`
      : ''
  }`;
  const isFormer = relationship.status === 'former';
  return (
    <section className="card relationship-card" data-component="relationship-card" aria-label={`${relationship.type}: ${relationship.organization || relationship.contact || 'Name to confirm'}`}>
      <div className="relationship-title">
        <span className="relationship-photo-wrap">
          <span className={symbolClass}>
            <Icon id="people" />
          </span>
        </span>
        <div>
          <span className="eyebrow">{relationship.type.toUpperCase()}</span>
          <h3>{relationship.organization || relationship.contact || 'Name to confirm'}</h3>
          <small className="relationship-contact-line">
            {relationship.contact || 'Contact not recorded'}
          </small>
        </div>
        <button
          type="button"
          className="text-btn"
          onClick={() => onEdit(relationship)}
        >
          Edit relationship
        </button>
      </div>
      <span className="role-tag">{relationship.specialty || 'Role to confirm'}</span>
      <div className="relationship-details">
        <div>
          <span>Location</span>
          <strong>{relationship.location || 'Not recorded'}</strong>
        </div>
        <div>
          <span>Linked horse</span>
          <strong>{linkedHorseName}</strong>
        </div>
        <div>
          <span>{isFormer ? 'Started' : 'Relationship since'}</span>
          <strong>{relationship.startDate || 'Not recorded'}</strong>
        </div>
        <div>
          <span>{isFormer ? 'Ended' : 'Status'}</span>
          <strong>
            {isFormer
              ? relationship.endedDate || 'Not recorded'
              : 'Current · Customer confirmed'}
          </strong>
        </div>
      </div>
      <div className="relationship-note">
        <span>RELATIONSHIP NOTES</span>
        <p>{relationship.notes || 'No notes yet'}</p>
      </div>
      {isFormer ? (
        <p className="end-note">
          Previous start: {relationship.startDate || 'Not recorded'}
          <br />
          Reason: {relationship.endReason || 'Not recorded'}
        </p>
      ) : null}
      <div className="relationship-foot">
        <span className={verificationPillClass(relationship.verification)}>
          {relationship.verification}
        </span>
        {!isFormer ? (
          <button
            type="button"
            className="end-link"
            onClick={() => onEnd(relationship)}
          >
            End or replace connection
          </button>
        ) : null}
        <small>Illustrative record &middot; No outreach sent</small>
      </div>
    </section>
  );
}
